
// Import supabase client
import { supabase } from '@/integrations/supabase/client';
import { getBookingGeographicData, BookingGeographicData } from './booking-analytics';

// User behavior analytics data helpers
// Integrates Google Analytics 4 data

export interface ConversionRateData {
  name: string;
  rate: number;
}

export interface PageTimeData {
  name: string;
  time: number; // seconds
}

export interface TrafficSourceData {
  name: string;
  value: number; // percentage
}

export interface DeviceTypeData {
  name: string;
  value: number; // percentage
}

export interface BrowserTypeData {
  name: string;
  value: number; // percentage
}

export interface GeographicData {
  country: string;
  sessions: number;
  users: number;
  percentage: number;
}

export interface UserBehaviorAnalyticsData {
  conversionRates: ConversionRateData[];
  pageTimes: PageTimeData[];
  trafficSources: TrafficSourceData[];
  deviceTypes: DeviceTypeData[];
  browserTypes: BrowserTypeData[];
  geographicDistribution: BookingGeographicData[];
  totalPageViews: number;
  averageSessionDuration: number;
  bounceRate: number;
  dataSource?: string; // 'google_analytics' or 'fallback_simulation'
  error?: string;
}

// Calculate safe conversion rate (max 100%)
const calculateSafeConversionRate = (numerator: number, denominator: number): number => {
  if (denominator === 0) return 0;
  const rate = (numerator / denominator) * 100;
  return Math.min(Math.round(rate * 100) / 100, 100); // Cap at 100%
};

/**
 * Fetch user behavior analytics data
 * Now retrieves real data from Google Analytics 4, falls back to simulated data on failure
 */
export const getUserBehaviorAnalyticsData = async (timeRange: string = 'month'): Promise<UserBehaviorAnalyticsData> => {
  try {
    // Fetch real GA4 data for web analytics
    const { data: ga4Data, error: ga4Error } = await supabase.functions.invoke('get-ga4-analytics', {
      body: { timeRange }
    });

    // Fetch real booking geographic data
    const bookingGeoData = await getBookingGeographicData(timeRange);

    if (ga4Error && bookingGeoData.length === 0) {
      console.error('Error fetching both GA4 and booking data:', ga4Error);
      return getFallbackAnalyticsData(timeRange);
    }

    // Combine GA4 data with booking geographic data
    if (ga4Data) {
      console.log('Successfully fetched GA4 analytics data with booking geography');
      return {
        ...ga4Data,
        geographicDistribution: bookingGeoData, // Replace GA4 geo data with booking data
        dataSource: bookingGeoData.length > 0 ? 'google_analytics_with_booking_geo' : 'google_analytics'
      };
    } else if (bookingGeoData.length > 0) {
      // If GA4 fails but we have booking data, use fallback with real booking geo
      const fallbackData = getFallbackAnalyticsData(timeRange);
      return {
        ...fallbackData,
        geographicDistribution: bookingGeoData,
        dataSource: 'fallback_with_booking_geo'
      };
    } else {
      return getFallbackAnalyticsData(timeRange);
    }
  } catch (error) {
    console.error('Error fetching GA4 analytics:', error);
    return getFallbackAnalyticsData(timeRange);
  }
};

/**
 * Get simulated data as fallback with realistic user journey funnel
 */
const getFallbackAnalyticsData = (timeRange: string): UserBehaviorAnalyticsData => {
  const timeMultiplier = getTimeMultiplier(timeRange);
  
  // Realistic user journey funnel based on actual flow with proper conversion logic
  const totalVisitors = Math.round(10000 * timeMultiplier);
  const findPractitionersVisitors = Math.round(totalVisitors * 0.35); // 35% go to find practitioners
  const practitionerProfileViews = Math.round(findPractitionersVisitors * 0.42); // 42% view a specific practitioner
  const bookingPageVisitors = Math.round(practitionerProfileViews * 0.28); // 28% start booking process
  const paymentPageVisitors = Math.round(bookingPageVisitors * 0.72); // 72% continue to payment
  const completedBookings = Math.round(paymentPageVisitors * 0.85); // 85% complete payment

  return {
    conversionRates: [
      { 
        name: 'Homepage → Find Practitioners', 
        rate: calculateSafeConversionRate(findPractitionersVisitors, totalVisitors)
      },
      { 
        name: 'Find Practitioners → Practitioner Profile', 
        rate: calculateSafeConversionRate(practitionerProfileViews, findPractitionersVisitors)
      },
      { 
        name: 'Practitioner Profile → Booking Start', 
        rate: calculateSafeConversionRate(bookingPageVisitors, practitionerProfileViews)
      },
      { 
        name: 'Booking Start → Payment Page', 
        rate: calculateSafeConversionRate(paymentPageVisitors, bookingPageVisitors)
      },
      { 
        name: 'Payment Page → Booking Complete', 
        rate: calculateSafeConversionRate(completedBookings, paymentPageVisitors)
      },
      { 
        name: 'Overall: Homepage → Booking Complete', 
        rate: calculateSafeConversionRate(completedBookings, totalVisitors)
      },
    ],
    pageTimes: [
      { name: 'Homepage', time: Math.round(145 * timeMultiplier) },
      { name: 'Find Practitioners', time: Math.round(220 * timeMultiplier) },
      { name: 'Practitioner Profile', time: Math.round(285 * timeMultiplier) },
      { name: 'Booking DateTime', time: Math.round(180 * timeMultiplier) },
      { name: 'Payment Page', time: Math.round(95 * timeMultiplier) },
      { name: 'Booking Success', time: Math.round(45 * timeMultiplier) },
    ],
    trafficSources: [
      { name: 'Direct', value: 35 },
      { name: 'Google Search', value: 28 },
      { name: 'Social Media', value: 18 },
      { name: 'Referral Sites', value: 12 },
      { name: 'Email Marketing', value: 7 },
    ],
    deviceTypes: [
      { name: 'Desktop', value: 52 },
      { name: 'Mobile', value: 38 },
      { name: 'Tablet', value: 10 },
    ],
    browserTypes: [
      { name: 'Chrome', value: 48 },
      { name: 'Safari', value: 28 },
      { name: 'Firefox', value: 12 },
      { name: 'Edge', value: 8 },
      { name: 'Other', value: 4 },
    ],
    geographicDistribution: [
      { country: 'United States', sessions: Math.round(450 * timeMultiplier), users: Math.round(380 * timeMultiplier), percentage: 35, bookingCount: Math.round(90 * timeMultiplier), totalRevenue: Math.round(9000 * timeMultiplier) },
      { country: 'China', sessions: Math.round(320 * timeMultiplier), users: Math.round(280 * timeMultiplier), percentage: 25, bookingCount: Math.round(64 * timeMultiplier), totalRevenue: Math.round(6400 * timeMultiplier) },
      { country: 'Canada', sessions: Math.round(180 * timeMultiplier), users: Math.round(160 * timeMultiplier), percentage: 14, bookingCount: Math.round(36 * timeMultiplier), totalRevenue: Math.round(3600 * timeMultiplier) },
      { country: 'United Kingdom', sessions: Math.round(120 * timeMultiplier), users: Math.round(100 * timeMultiplier), percentage: 9, bookingCount: Math.round(24 * timeMultiplier), totalRevenue: Math.round(2400 * timeMultiplier) },
      { country: 'Australia', sessions: Math.round(90 * timeMultiplier), users: Math.round(75 * timeMultiplier), percentage: 7, bookingCount: Math.round(18 * timeMultiplier), totalRevenue: Math.round(1800 * timeMultiplier) },
      { country: 'Germany', sessions: Math.round(70 * timeMultiplier), users: Math.round(60 * timeMultiplier), percentage: 5, bookingCount: Math.round(14 * timeMultiplier), totalRevenue: Math.round(1400 * timeMultiplier) },
      { country: 'Japan', sessions: Math.round(50 * timeMultiplier), users: Math.round(45 * timeMultiplier), percentage: 4, bookingCount: Math.round(10 * timeMultiplier), totalRevenue: Math.round(1000 * timeMultiplier) },
      { country: 'France', sessions: Math.round(20 * timeMultiplier), users: Math.round(18 * timeMultiplier), percentage: 1, bookingCount: Math.round(4 * timeMultiplier), totalRevenue: Math.round(400 * timeMultiplier) }
    ],
    totalPageViews: Math.round(15420 * timeMultiplier),
    averageSessionDuration: Math.round(280 * timeMultiplier),
    bounceRate: Math.round(42 / timeMultiplier),
    dataSource: 'fallback_simulation'
  };
};

// Get data adjustment multiplier based on time range
const getTimeMultiplier = (timeRange: string): number => {
  switch (timeRange) {
    case 'week':
      return 0.25;
    case 'month':
      return 1;
    case 'quarter':
      return 2.8;
    case 'year':
      return 10.5;
    default:
      return 1;
  }
};

/**
 * Format session duration for display
 */
export const formatSessionDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * Get conversion rate color (for chart display)
 */
export const getConversionRateColor = (rate: number): string => {
  if (rate >= 20) return '#22c55e'; // Green - high conversion rate
  if (rate >= 10) return '#3b82f6'; // Blue - medium conversion rate
  if (rate >= 5) return '#f59e0b'; // Yellow - lower conversion rate
  return '#ef4444'; // Red - low conversion rate
};

/**
 * Calculate time period comparison data (for showing growth trends)
 */
export const calculateGrowthRate = (current: number, previous: number): number => {
  if (previous === 0) return 0;
  return Math.round(((current - previous) / previous) * 100);
};

